import type { Icon } from "@tabler/icons-react";
import { type AdminAccess, hasAdminFeature } from "@/utils/admin-access";
import type { AdminMenuFeatureKey } from "@/utils/permission.features";

export interface AdminNavLeaf {
	icon: Icon;
	label: string;
	to: string;
	description: string;
	/** Kunci izin menu; tanpa ini item selalu tampil (mis. Beranda panel admin). */
	feature?: AdminMenuFeatureKey;
}

export interface AdminNavGroup {
	icon: Icon;
	label: string;
	description: string;
	children: AdminNavLeaf[];
}

export type AdminNavItem = AdminNavLeaf | AdminNavGroup;

export function isNavGroup(item: AdminNavItem): item is AdminNavGroup {
	return "children" in item;
}

/** Apakah `path` menu aktif untuk lokasi `current`; `/admin` selalu persis, selain itu pencocokan prefiks. */
export function isNavPathActive(current: string, path: string): boolean {
	if (path === "/admin") return current === "/admin" || current === "/admin/";
	return current.startsWith(path);
}

/** Grup aktif bila salah satu anaknya aktif. */
export function isNavGroupActive(
	current: string,
	group: AdminNavGroup,
): boolean {
	return group.children.some((c) => isNavPathActive(current, c.to));
}

/** Item daun tampil bila tidak punya kunci fitur, atau fiturnya diizinkan. */
function isLeafVisible(leaf: AdminNavLeaf, access: AdminAccess): boolean {
	return leaf.feature === undefined || hasAdminFeature(access, leaf.feature);
}

/**
 * Saring sidebar admin menurut izin: grup disaring per anak dan ikut hilang
 * kalau tidak ada anak yang tersisa (grup tanpa `to` tidak bisa dibuka).
 * Menyembunyikan menu TIDAK mengamankan apa pun — endpoint & `beforeLoad`
 * route yang menolaknya; ini hanya supaya sidebar tidak memamerkan menu mati.
 */
export function filterAdminNav(
	items: readonly AdminNavItem[],
	access: AdminAccess,
): AdminNavItem[] {
	const visible: AdminNavItem[] = [];
	for (const item of items) {
		if (!isNavGroup(item)) {
			if (isLeafVisible(item, access)) visible.push(item);
			continue;
		}
		const children = item.children.filter((c) => isLeafVisible(c, access));
		if (children.length > 0) visible.push({ ...item, children });
	}
	return visible;
}
